import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { IHash } from "@bbwt/interfaces";
import { BehaviorSubject } from "rxjs";
import { SqlQueryBuilderService } from "../api/sql-query-builder.service";
import { VariablesService } from "@main/reporting.v3/api/variables.service";
import {
    EditionFunc,
    IQuerySource,
    IVariableInfo,
    ITableSetTable,
    ITableSetColumn,
    ITableSetFolderInfo,
} from "@main/reporting.v3/core/reporting-models";
import { ISqlQueryBuild } from "../query-builder-models";
import { TableSetService } from "@main/reporting.v3/api/table-set.service";
import {
    QueryTableSelectorComponent
} from "@main/reporting.v3/query-table-selector/components/query-table-selector.component";
import { CodeEditorHighlightService } from "@controls/text-editors/services/code-editor-highlight.service";
import { ContextVariablesService } from "@main/reporting.v3/query-builder/api/context-variables.service";
import {
    SqlSchema,
    SqlDialectType,
    SqlLanguageSpec,
    SqlCompletionConfig,
    SqlSchemaCompletionConfig,
    SqlVariableCompletionConfig,
} from "@controls/text-editors/sql-editor/models";
import {
    SqlEditorSchemaCompletionService
} from "@controls/text-editors/sql-editor/services/sql-editor-completion-schema.service";
import {
    SqlEditorVariableCompletionService
} from "@controls/text-editors/sql-editor/services/sql-editor-completion-variable.service";
import { SqlEditorCompletionService } from "@controls/text-editors/sql-editor/services/sql-editor-completion.service";
import { CodeEditorKeyboardService } from "@controls/text-editors/services/code-editor-keyboard.service";
import { SqlEditorComponent } from "@controls/text-editors/sql-editor/components/sql-editor.component";
import { RegexUtilityService } from "@main/reporting.v3/core/regex/regex-utility.service";
import { Compartment, Extension, StateEffect } from "@codemirror/state";
import { CodeEditorHighlightPlugin } from "@controls/text-editors/models";
import { CompletionSource } from "@codemirror/autocomplete";
import { KeyBinding, ViewPlugin } from "@codemirror/view";
import { DatabaseType } from "@main/dbdoc";


@Component({
    selector: "query-builder-sql-editor",
    templateUrl: "./query-builder-sql-editor.component.html",
    styleUrls: ["./query-builder-sql-editor.component.scss"]
})
export class QueryBuilderSqlEditorComponent implements OnInit {
    // Core editor settings.
    protected sqlCode: string;
    protected editorDisabled: boolean;

    // Loading settings.
    public loading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    // Table set features settings.
    protected folderId: string;

    // Column settings.
    protected columns: IHash<ITableSetColumn[]> = {};

    // Context variables features settings.
    protected contextVariables: string[] = [];

    // Variables features settings.
    protected variables: IVariableInfo[] = [];

    // Tables settings.
    protected tables: ITableSetTable[] = [];

    // Sql editor extensions.
    protected extensions: Extension[];

    protected keyboard: Extension;
    protected highlight: Extension;
    protected autocompletion: Extension;

    protected compartment: Compartment = new Compartment();

    // Sql editor language configurations.
    protected specification: SqlLanguageSpec;
    protected dialect: SqlDialectType;
    protected schemas: SqlSchema[];

    private _querySource: IQuerySource = this.defaultQuerySource;
    private _sqlQueryBuild: ISqlQueryBuild = this.defaultSqlQueryBuild;

    @Output() readonly updateQuerySource: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly querySourceChange: EventEmitter<IQuerySource> = new EventEmitter<IQuerySource>();

    @ViewChild(SqlEditorComponent) private _sqlEditor: SqlEditorComponent;
    @ViewChild(QueryTableSelectorComponent) private _queryTableSelector: QueryTableSelectorComponent;

    constructor(private contextVariablesService: ContextVariablesService,
                private completionService: SqlEditorCompletionService,
                private schemaCompletionService: SqlEditorSchemaCompletionService,
                private variableCompletionService: SqlEditorVariableCompletionService,
                private highlightService: CodeEditorHighlightService,
                private keyboardService: CodeEditorKeyboardService,
                private regexUtilityService: RegexUtilityService,
                private querySqlService: SqlQueryBuilderService,
                private variablesService: VariablesService,
                private tableSetService: TableSetService) {
    }

    // Sql-query-build for updating purposes.
    set sqlQueryBuild(value: ISqlQueryBuild) {
        this._sqlQueryBuild = value ?? this.defaultSqlQueryBuild;
        this._querySource = this._sqlQueryBuild.querySource;
        this.sqlCode = this._sqlQueryBuild.sqlCode;
        this.querySourceChange.emit(this._querySource);
    }

    get sqlQueryBuild(): ISqlQueryBuild {
        return this._sqlQueryBuild;
    }

    // Query source for updating purposes.
    @Input() set querySource(value: IQuerySource) {
        if (this.querySource === value) return;
        this._querySource = value ?? this.defaultQuerySource;
        this.querySourceChange.emit(this._querySource);
        this.refreshQuery().then();
    }

    get querySource(): IQuerySource {
        return this._querySource;
    }

    // Variable to be able to externally disable SQL editor.
    @Input() set disabled(value: boolean) {
        this.editorDisabled = value;
    }

    get disabled(): boolean {
        // Query editor is disabled if it was disabled externally or if this
        // query is not an SQL query (has no associated SQL query edition object).
        return this.editorDisabled || !this._sqlQueryBuild;
    }

    get querySourceId(): string {
        return this._querySource?.id;
    }

    get defaultSqlQueryBuild(): ISqlQueryBuild {
        return { querySource: this.defaultQuerySource, sqlCode: "" } as ISqlQueryBuild;
    }

    get defaultQuerySource(): IQuerySource {
        return { queryType: "sql" } as IQuerySource;
    }

    get isDraftQuery(): boolean {
        return !!this._querySource.isDraft;
    }

    get dirty(): boolean {
        const modifiedCode: string = this.sqlCode?.trim();
        const originalCode: string = this._sqlQueryBuild?.sqlCode?.trim();

        // If at least one of the strings is not empty, they are different,
        // and the editor is not disabled, then consider it dirty.
        return (!!modifiedCode?.length || !!originalCode?.length)
            && modifiedCode !== originalCode && !this.disabled;
    }


    async ngOnInit(): Promise<void> {
        await this.refreshContextVariables();
        await this.refreshVariables();

        this.refreshLanguage();

        this.configureKeyboard();
        this.configureHighlighting();
        this.configureAutocompletion();

        this.extensions = [
            this.autocompletion,
            this.highlight,
            this.keyboard,
        ];
    }

    // Configure variables highlighting.
    private getEmittedVariablesHighlight(): ViewPlugin<CodeEditorHighlightPlugin> {
        if (!this.variables?.length) return ViewPlugin.fromClass(CodeEditorHighlightPlugin);

        const names: string[] = this.variables.map(variable => variable.name);
        const escaped: string[] = this.regexUtilityService.escapeRegexes(names);

        const concat: string = escaped.join("|");
        const pattern: string = `\\B#(${concat})\\b`;

        const classes: string = "sql-known-variable sql-emitted-variable";

        return this.highlightService.getHighlightExtension(pattern, classes);
    }

    private getContextVariablesHighlight(): ViewPlugin<CodeEditorHighlightPlugin> {
        if (!this.contextVariables?.length) return ViewPlugin.fromClass(CodeEditorHighlightPlugin);

        const names: string[] = this.contextVariables.map(variable => variable);
        const escaped: string[] = this.regexUtilityService.escapeRegexes(names);

        const concat: string = escaped.join("|");
        const pattern: string = `\\B@(${concat})\\b`;

        const classes: string = "sql-known-variable sql-context-variable";

        return this.highlightService.getHighlightExtension(pattern, classes);
    }

    private getVariablesHighlight(): ViewPlugin<CodeEditorHighlightPlugin> {
        const pattern: string = "\\B[@#]\\S+\\b";

        const classes: string = "sql-variable";

        return this.highlightService.getHighlightExtension(pattern, classes);
    }

    private configureHighlighting(): void {
        this.highlight = [
            this.getEmittedVariablesHighlight(),
            this.getContextVariablesHighlight(),
            this.getVariablesHighlight(),
        ]
    }

    // Configure hotkeys.
    private getCtrlEnterKeyboardHandler(): KeyBinding {
        return {
            key: "Mod-Enter",
            run: (): boolean => {
                // Update this query source preview.
                this.updateQuerySource.emit()

                // Capture this key (return true to stop bubbling).
                return true;
            }
        }
    }

    private configureKeyboard(): void {
        this.keyboard = this.keyboardService.getKeyboardExtension([
            this.getCtrlEnterKeyboardHandler(),
        ])
    }

    // Configure autocompletion.
    private getEmittedVariableCompletionSource(): CompletionSource {

        const config: SqlVariableCompletionConfig = {
            prefix: "#",
            variables: this.variables.map(variable => variable.name),
        }

        return this.variableCompletionService.getVariableCompletionSource(config);
    }

    private getContextVariableCompletionSource(): CompletionSource {

        const config: SqlVariableCompletionConfig = {
            prefix: "@",
            variables: this.contextVariables,
        }

        return this.variableCompletionService.getVariableCompletionSource(config);
    }

    private getSchemaCompletionSource(): CompletionSource {

        const config: SqlSchemaCompletionConfig = {
            spec: this.specification,
            getChildren: schema => this.refreshColumns(schema.name),
        }

        return this.schemaCompletionService.getLazySchemaCompletionSource(config);
    }

    private getAutocompletionExtension(): Extension {

        const config: SqlCompletionConfig = {
            spec: this.specification,
            sources: [
                this.getEmittedVariableCompletionSource(),
                this.getContextVariableCompletionSource(),
                this.getSchemaCompletionSource(),
            ]
        }

        return this.completionService.getCompletionExtension(config);
    }

    private configureAutocompletion(): void {
        const autocompletion: Extension = this.getAutocompletionExtension();

        this.autocompletion = this.compartment.of(autocompletion);
    }

    // Refreshing methods.
    private refreshLanguage(): void {
        this.specification = {
            dialect: this.dialect,
            spec: {
                specialVar: "@#",
                hashComments: false,
            },
            options: {
                upperCaseKeywords: true
            },
            schemas: this.schemas ?? []
        };
    }

    private refreshAutocompletion(): void {
        if (!this._sqlEditor) return;

        const autocompletion: Extension = this.getAutocompletionExtension();

        const effect: StateEffect<any> = this.compartment.reconfigure(autocompletion);

        this._sqlEditor.dispatch({ effects: effect });
    }

    private async refreshQuery(): Promise<void> {
        if (!this.querySourceId) return;
        this.sqlQueryBuild = await this.querySqlService.getBuild(this.querySourceId);
    }

    private async refreshVariables(): Promise<void> {
        this.variables = await this.variablesService.getAllVariablesInfo() ?? [];

        this.refreshAutocompletion();
    }

    private async refreshContextVariables(): Promise<void> {
        this.contextVariables = (await this.contextVariablesService.getVariableNames()) ?? [];

        this.refreshAutocompletion();
    }

    protected refreshFolder(folderInfo: ITableSetFolderInfo): void {
        this.folderId = folderInfo?.id;
        this.tables = folderInfo?.tables ?? [];
        this.schemas = folderInfo?.tables?.map(table => <SqlSchema>{ name: table.tableAlias }) ?? [];

        switch (folderInfo?.databaseType) {
            case DatabaseType.MySql:
                this.dialect = SqlDialectType.MySQL;
                break;
            case DatabaseType.MsSql:
                this.dialect = SqlDialectType.MSSQL;
                break;
            case DatabaseType.PostgreSql:
                this.dialect = SqlDialectType.PostgresSQL;
                break;
            default:
                this.dialect = SqlDialectType.StandardSQL;
                break;
        }

        this.refreshLanguage();
        this.refreshAutocompletion();
    }

    private async refreshColumns(name: string): Promise<SqlSchema[]> {
        if (!name?.length) return [];

        const table: ITableSetTable = this.tables.find(table =>
            table.tableAlias.toLowerCase() === name.toLowerCase());

        if (!table) return;

        this.columns[table.id] ??= await this.tableSetService
            .getTableColumns(table.sourceCode, this.folderId, table.id);

        const columns: ITableSetColumn[] = this.columns[table.id];

        if (!columns?.length) return [];

        // All columns of this table. Instance its children with an empty array
        // to prevent autocompletion feature to try to load children for these schemas.
        return columns.map(column => <SqlSchema>{ name: column.columnAlias, children: [] });
    }

    // Insertion methods.
    protected insertTableNodeValue(table: ITableSetTable): void {
        if (!table?.tableAlias?.length) return;

        this.insertTable(table.tableAlias);
    }

    protected insertColumnNodeValue(column: ITableSetColumn): void {
        if (!column?.columnAlias?.length) return;

        const table: ITableSetTable = column.table;

        if (!table?.tableAlias?.length) return;

        this.insertColumn(table.tableAlias, column.columnAlias);
    }

    protected insertTable(table: string): void {
        if (!this._sqlEditor || !table?.length) return;

        const empty: boolean = !this.sqlCode?.trim().length;

        const text: string = empty ? `SELECT * FROM ${table}` : table;

        this._sqlEditor.insert(text);
    }

    protected insertColumn(table: string, column: string): void {
        if (!this._sqlEditor || !table?.length || !column?.length) return;

        const empty: boolean = !this.sqlCode?.trim().length;

        const text: string = empty ? `SELECT ${table}.${column} FROM ${table}` : `${table}.${column}`;

        this._sqlEditor.insert(text);
    }

    // Edition methods.
    public async getSqlQueryBuild(): Promise<ISqlQueryBuild> {
        if (!this.sqlQueryBuild) return null;

        const tableSetId: string = await this._queryTableSelector.save();

        return {
            id: this.sqlQueryBuild?.id,
            sqlCode: this.sqlCode,
            tableSetId: tableSetId,
            querySource: this._querySource,
            querySourceId: this._querySource?.id
        };
    }

    public async createDraft(): Promise<string> {
        if (this.loading.value) return;

        const querySourceId: string = this.isDraftQuery ? this._querySource.releaseQueryId : this.querySourceId;
        const editionFunc: EditionFunc<ISqlQueryBuild> =
            (sqlQuery: ISqlQueryBuild): Promise<ISqlQueryBuild> =>
                this.querySqlService.createDraft(sqlQuery, querySourceId);

        return this.editQuery(editionFunc);
    }

    public async releaseDraft(): Promise<string> {
        if (this.loading.value || !this.querySourceId) return;

        const editionFunc: EditionFunc<ISqlQueryBuild> =
            (sqlQuery: ISqlQueryBuild): Promise<ISqlQueryBuild> =>
                this.querySqlService.update(sqlQuery.id, sqlQuery)
                    .then(build => this.querySqlService.releaseDraft(build.querySourceId)
                        .then(querySourceId => this.querySqlService.getBuild(querySourceId)));

        return this.editQuery(editionFunc);
    }

    public async save(): Promise<string> {
        if (this.loading.value) return;

        const editionFunc: EditionFunc<ISqlQueryBuild> =
            (sqlQuery: ISqlQueryBuild): Promise<ISqlQueryBuild> =>
                !this.querySourceId
                    ? this.querySqlService.create(sqlQuery)
                    : this.querySqlService.update(sqlQuery.id, sqlQuery);

        return this.editQuery(editionFunc);
    }

    private async editQuery(editionFunc: EditionFunc<ISqlQueryBuild>): Promise<string> {
        if (this.loading.value) return;

        try {
            this.loading.next(true);
            const sqlQuery: ISqlQueryBuild = await this.getSqlQueryBuild();

            // Try to edit the query. Restore sql query if edition fails.
            const build: ISqlQueryBuild = await editionFunc(sqlQuery).catch(_ => null);

            // Update the build only if there was no error.
            if (build != null) this.sqlQueryBuild = build;

            return build?.querySourceId ?? null;

        } finally {
            this.loading.next(false);
        }
    }
}